<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-table-filter :filter-title="$t('expensa.menu_exports')">
    <oxd-form @submit-valid="onDownload('csv')">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.status"
              type="select"
              :label="$t('general.status')"
              :options="statusOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.kind"
              type="select"
              :label="$t('expensa.col_kind')"
              :options="kindOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.category"
              type="select"
              :label="$t('expensa.col_category')"
              :options="categoryOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="showEmployeeFilter">
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{includeEmployees: 'currentAndPast'}"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="filters.fromDate"
              :label="$t('general.from_date')"
              :rules="rules.fromDate"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.toDate"
              :label="$t('general.to_date')"
              :rules="rules.toDate"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.q"
              :label="$t('expensa.filter_text')"
              :rules="rules.q"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.unreported"
              type="checkbox"
              :true-value="true"
              :false-value="false"
              :option-label="$t('expensa.filter_unreported')"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          @click="onClickReset"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('expensa.exp_download_csv')"
          :disabled="busy"
          type="submit"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('expensa.exp_download_xlsx')"
          :disabled="busy"
          type="button"
          @click="onClickXlsx"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <template v-if="me && me.isFinance">
    <br />
    <oxd-table-filter :filter-title="$t('expensa.exp_journal_title')">
      <oxd-form @submit-valid="onDownloadJournal">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="journal.fromDate"
                :label="$t('general.from_date')"
                :rules="rules.journalFrom"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="journal.toDate"
                :label="$t('general.to_date')"
                :rules="rules.journalTo"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="secondary"
            :label="$t('expensa.exp_download_journal')"
            :disabled="busy"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
  </template>
</template>

<script>
import {ref} from 'vue';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  endDateShouldBeAfterStartDate,
  required,
  shouldNotExceedCharLength,
  validDateFormat,
  validSelection,
} from '@/core/util/validation/rules';
import {expensaApi, fileUrl} from '../util/api';
import {downloadExport, exportQuery} from '../util/download';
import {
  EXPENSE_KINDS,
  EXPENSE_STATUSES,
  expenseStatusKey,
  kindKey,
} from '../util/labels';

const defaultFilters = () => ({
  status: null,
  kind: null,
  category: null,
  employee: null,
  fromDate: null,
  toDate: null,
  q: '',
  unreported: false,
});

export default {
  name: 'ExpensaExports',
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    return {
      userDateFormat,
      filters: ref(defaultFilters()),
      journal: ref({fromDate: null, toDate: null}),
    };
  },
  data() {
    return {
      me: null,
      categories: [],
      busy: false,
    };
  },
  computed: {
    rules() {
      return {
        employee: [validSelection],
        q: [shouldNotExceedCharLength(100)],
        fromDate: [validDateFormat(this.userDateFormat)],
        toDate: [
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.filters.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
        journalFrom: [required, validDateFormat(this.userDateFormat)],
        journalTo: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.journal.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
      };
    },
    showEmployeeFilter() {
      return !!this.me && (this.me.isFinance || this.me.isSupervisor);
    },
    statusOptions() {
      return EXPENSE_STATUSES.map((id) => ({
        id,
        label: this.$t(expenseStatusKey(id)),
      }));
    },
    kindOptions() {
      return EXPENSE_KINDS.map((id) => ({id, label: this.$t(kindKey(id))}));
    },
    categoryOptions() {
      return this.categories.map((c) => ({id: c.id, label: c.name}));
    },
  },
  beforeMount() {
    expensaApi('/me')
      .getAll()
      .then(({data}) => {
        this.me = data.data;
      })
      .catch(() => {
        // senza `me` restano nascosti filtro dipendente e prima nota
      });
    expensaApi('/categories')
      .getAll({includeInactive: true})
      .then(({data}) => {
        this.categories = data.data;
      })
      .catch(() => {
        // senza categorie il filtro resta vuoto
      });
  },
  methods: {
    onClickReset() {
      this.filters = defaultFilters();
    },
    onClickXlsx() {
      this.onDownload('xlsx');
    },
    expensesUrl(format) {
      const f = this.filters;
      return fileUrl(
        exportQuery('/expensa/export/expenses', {
          format,
          status: f.status?.id,
          kind: f.kind?.id,
          categoryId: f.category?.id,
          empNumber: f.employee?.id,
          fromDate: f.fromDate,
          toDate: f.toDate,
          q: f.q ? f.q.trim() : null,
          unreported: f.unreported ? 'true' : null,
        }),
      );
    },
    onDownload(format) {
      return this.run(this.expensesUrl(format), 'spese.' + format);
    },
    onDownloadJournal() {
      const url = fileUrl(
        exportQuery('/expensa/export/journal', {
          fromDate: this.journal.fromDate,
          toDate: this.journal.toDate,
        }),
      );
      return this.run(url, 'prima_nota.csv');
    },
    // Prima una fetch: un errore 422/403 arriva come JSON e va mostrato,
    // non scaricato come file.
    async run(url, fallbackName) {
      if (this.busy) return;
      this.busy = true;
      try {
        await downloadExport(
          url,
          fallbackName,
          this.$t('expensa.exp_failed'),
          this.$t('expensa.exp_session_expired'),
        );
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.exp_done'),
        });
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message:
            e instanceof Error ? e.message : this.$t('expensa.exp_failed'),
        });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
